import { isSameDay, monthGridDays, toDateKey, type EventOccurrence } from '@awwyeah/shared';
import { barFill, daySummary, type DayMark } from '../dayMarks';
import { styleForOwners, UNASSIGNED_DOT_CLASS, type MemberView } from '../members';
import { shortTime } from '../weekBlocks';
import { useWeekStart, weekdayInitials } from '../weekStartPreference';

export interface MonthViewProps {
  month: Date;
  today: Date;
  selectedDay: Date;
  byDay: ReadonlyMap<string, EventOccurrence[]>;
  birthdayDays: ReadonlySet<string>;
  members: ReadonlyMap<string, MemberView>;
  onSelectDay: (day: Date) => void;
}

/** Phone cells: up to four bars, or three and a "+N". */
const PHONE_MARKS = 4;
/** sm and up: three titles, then "+N more". */
const WIDE_TITLES = 3;

/** A band that carries on into the neighbouring day runs to the cell edge, square-ended,
 * so a trip reads as one bar across the week row rather than a row of separate pills. */
function continuationClasses(mark: DayMark): string {
  return `${mark.continuesBefore ? '-ml-1.5 rounded-l-none' : ''} ${
    mark.continuesAfter ? '-mr-1.5 rounded-r-none' : ''
  }`;
}

/**
 * Month grid (design Screen C · 5). Every event gets a mark, ordered bands-first then by
 * time (see `daySummary`): stacked owner-colour bars at phone width, truncated titles on
 * wider screens, a quiet "+N" for the rest. Today keeps an inset ink frame so it survives
 * any filter; tapping anywhere in a cell still opens that day.
 */
export function MonthView({
  month,
  today,
  selectedDay,
  byDay,
  birthdayDays,
  members,
  onSelectDay,
}: MonthViewProps): JSX.Element {
  const { weekStartsOn } = useWeekStart();
  const days = monthGridDays(month, weekStartsOn);
  const WEEKDAY_INITIALS = weekdayInitials(weekStartsOn);

  return (
    <div>
      <div className="grid grid-cols-7 border-b border-line">
        {WEEKDAY_INITIALS.map((initial, index) => (
          <span
            key={`${initial}-${index}`}
            className="py-1 text-center text-[11px] font-semibold text-fg-3"
          >
            {initial}
          </span>
        ))}
      </div>

      <div
        className="grid grid-cols-7 gap-px bg-line"
        data-testid="calendar-grid"
        role="grid"
        aria-label="Month"
      >
        {days.map((day) => {
          const key = toDateKey(day);
          const occurrences = byDay.get(key) ?? [];
          const outside = day.getMonth() !== month.getMonth();
          const isToday = isSameDay(day, today);
          const isSelected = isSameDay(day, selectedDay);
          const bars = daySummary(occurrences, day, PHONE_MARKS);
          const titles = daySummary(occurrences, day, WIDE_TITLES, { overflowTakesSlot: false });
          const birthday = birthdayDays.has(key);

          return (
            <button
              key={key}
              type="button"
              role="gridcell"
              aria-label={`${key}, ${occurrences.length} events`}
              aria-selected={isSelected}
              data-testid="calendar-day-cell"
              data-date={key}
              onClick={() => onSelectDay(day)}
              className={`flex min-h-[64px] min-w-0 flex-col gap-1 p-1.5 text-left transition-colors duration-150 ease-app sm:min-h-[92px] ${
                outside ? 'bg-bg' : 'bg-surface'
              } ${isSelected ? 'ring-2 ring-inset ring-fg' : ''}`}
            >
              <span className="flex items-center gap-1">
                <span
                  className={`text-[13px] leading-tight ${outside ? 'text-fg-3' : ''} ${
                    isToday
                      ? 'flex h-[22px] w-[22px] items-center justify-center rounded-full bg-inverse text-[12px] font-bold text-inverse-fg'
                      : ''
                  } ${birthday && !isToday ? 'border-b-2 border-accent' : ''}`}
                >
                  {day.getDate()}
                </span>
                {birthday && (
                  <span
                    className="h-[7px] w-[7px] shrink-0 rounded-full border-2 border-accent"
                    title="Birthday"
                  />
                )}
              </span>

              {/* Phone: one thin bar per event, so four events look like four things. */}
              {bars.total > 0 && (
                <span className="flex flex-col gap-[2px] sm:hidden" aria-hidden="true">
                  {bars.marks.map((mark) => {
                    const unassigned = mark.occurrence.event.ownerIds.length === 0;
                    return (
                      <span
                        key={mark.occurrence.key}
                        data-testid="day-bar"
                        data-unassigned={unassigned || undefined}
                        style={barFill(mark.occurrence.event.ownerIds, members)}
                        className={`h-1 rounded-[2px] ${unassigned ? UNASSIGNED_DOT_CLASS : ''} ${
                          mark.band ? continuationClasses(mark) : ''
                        }`}
                      />
                    );
                  })}
                  {bars.hidden > 0 && (
                    <span className="font-mono text-[9px] font-bold leading-none text-fg-3">
                      +{bars.hidden}
                    </span>
                  )}
                </span>
              )}

              {/* Wider screens: a few titles on the owner's colour edge — bands (all-day,
                  multi-day) get the soft tint, timed events stay on the surface. */}
              {titles.total > 0 && (
                <span className="hidden min-w-0 flex-col gap-px sm:flex" aria-hidden="true">
                  {titles.marks.map((mark) => {
                    const { event } = mark.occurrence;
                    const style = styleForOwners(event.ownerIds, members);
                    return (
                      <span
                        key={mark.occurrence.key}
                        data-testid="day-title"
                        style={mark.band ? { ...style.edge, ...style.tint } : style.edge}
                        className={`block truncate rounded-[2px] border-l-2 pl-1 pr-0.5 text-[11px] leading-[15px] ${
                          mark.band
                            ? `font-semibold text-fg ${continuationClasses(mark)}`
                            : 'text-fg-2'
                        }`}
                      >
                        {!mark.band && (
                          <span className="hidden font-mono text-[10px] text-fg-3 md:inline">
                            {shortTime(mark.occurrence.start)}{' '}
                          </span>
                        )}
                        {event.title}
                      </span>
                    );
                  })}
                  {titles.hidden > 0 && (
                    <span className="pl-1 text-[10px] leading-[14px] text-fg-3">
                      +{titles.hidden} more
                    </span>
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
